import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "../lib/api";

const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const fmtMonth = (ym) => `${MONTHS[Number(ym.slice(5, 7)) - 1]} ${ym.slice(0, 4)}`;

// Hücre durumu: maliyet girildi → yeşil; değilse hangi hakedişin eksik olduğu renkle gösterilir (müşteri → taşeron → maliyet sırası)
const STATES = {
  done: { label: "✓", cls: "bg-emerald-500 text-white hover:bg-emerald-700", title: "maliyet girildi", action: "tıkla → ilgili maliyet kaydına git" },
  cust_none: { label: "MÜŞ. YOK", cls: "bg-slate-600 text-white hover:bg-slate-800", title: "müşteri hakedişi oluşturulmamış", action: "tıkla → Müşteri Hakedişi ekranı (proje, ana başlık ve dönem seçili açılır)" },
  cust_draft: { label: "MÜŞ. TASLAK", cls: "bg-amber-500 text-white hover:bg-amber-700", title: "müşteri hakedişi taslak — faturası onaylanmamış", action: "tıkla → Müşteri Hakedişi ekranında 'Hakedişi Onayla'" },
  sub_none: { label: "TAŞ. YOK", cls: "bg-orange-500 text-white hover:bg-orange-700", title: "taşeron hakedişi oluşturulmamış", action: "tıkla → Taşeron Hakedişi ekranı (proje, ana başlık ve dönem seçili açılır)" },
  sub_draft: { label: "TAŞ. TASLAK", cls: "bg-yellow-500 text-slate-900 hover:bg-yellow-600", title: "taşeron hakedişi taslak — faturası onaylanmamış", action: "tıkla → taşeron faturası açılır, bütçe kalemi + KDV seçip Kaydet ile onaylayın" },
  cost: { label: "YOK", cls: "bg-red-500 text-white hover:bg-red-700", title: "hakedişler hazır — maliyet girilmemiş", action: "tıkla → ilgili maliyet girişine git" },
};
const stateOf = (ok, cs) => {
  if (ok) return "done";
  if (!cs) return "cost";
  if (cs.customer === "none") return "cust_none";
  if (cs.customer === "draft") return "cust_draft";
  if (cs.sub === "none") return "sub_none";
  if (cs.sub === "draft") return "sub_draft";
  return "cost";
};
const monthSpan = (m) => { const [y, mo] = m.split("-").map(Number); return `bas=${m}-01&bit=${m}-${String(new Date(y, mo, 0).getDate()).padStart(2, "0")}`; };

// Hücreye tıklayınca: maliyet eksikse onSelectMonth(p, ay, kalem, odakAnahtarı) (maliyet girişi); hakediş eksik / taslaksa doğrudan ilgili
// hakediş ekranına / faturaya gidilir — ?don= ile dönüşte (ESC / Kaydet / Onayla) aynı hücreye odaklanılır (data-odak)
export const MissingCostProjects = ({ period, selectedId, onSelect, onSelectMonth, refreshKey, testid = "missing-cost-projects" }) => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    http.get("/project-costs/missing-projects", { params: period ? { period } : {} }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [period, refreshKey]);
  if (!data || !data.projects?.length) return null;
  const act = (p, m, c, key, state, cs) => {
    if (state === "cost" || state === "done") return (onSelectMonth || onSelect)?.(p, m, c, key);   // maliyet girişi / mevcut maliyet kaydı
    const u = new URL(window.location.href); u.searchParams.set("odak", key);
    const don = `&don=${encodeURIComponent(u.pathname + u.search)}`;
    if (state === "sub_draft" && cs?.sub_id) return navigate(`/veri-girisi?bolum=fatura&fatura=${cs.sub_id}${don}`);
    navigate(`/${state.startsWith("cust") ? "musteri" : "taseron"}-hakedis?proje=${p.id}${cs?.category_id ? `&ana=${cs.category_id}` : ""}&${monthSpan(m)}${don}`);
  };
  return (
    <div className="bg-white border border-red-200 rounded-lg px-4 py-3 space-y-2" data-testid={testid}>
      <div className="text-xs font-bold text-red-700 uppercase tracking-wide">Maliyeti Eksik Projeler ({data.period}) — {data.count}</div>
      <div className="text-[10px] text-slate-500">Proje maliyeti tüm ana kalemleri kapsar: her kalemin (Asıl Personel, Geçici Personel, ATM, Ekstra Temizlik…) maliyeti ayrı girilir; biri eksikse proje bu listede kalır. Muaf kalemler Proje Kartı'ndan ayarlanır. Eksik aylar başlangıç tarihinden bugüne hesaplanır. Hücreye tıklayınca eksik adımın ekranı açılır; ESC / Kaydet ile buraya dönülür.</div>
      <div className="flex flex-wrap items-center gap-1.5 text-[10px]" data-testid={`${testid}-legend`}>
        <span className="text-slate-500 font-semibold uppercase tracking-wide mr-1">Hücre rengi:</span>
        {Object.entries(STATES).map(([k, s]) => <span key={k} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-semibold ${s.cls.split(" hover:")[0]}`} title={s.action || s.title}>{s.label}<span className="font-normal opacity-80">— {s.title}</span></span>)}
      </div>
      <ul className="grid grid-cols-1 gap-2">
        {data.projects.map((p) => {
          const sel = selectedId === p.id;
          const months = p.all_months || p.missing_months || [];
          const cats = p.categories || [];
          const isDone = (c, m) => (p.done_by_month?.[m] || []).includes(c);
          const catStatus = Object.fromEntries((p.missing_categories || []).map((c) => [c.category, c]));
          return (
            <li key={p.id} className={`rounded border overflow-hidden ${sel ? "border-orange-500 ring-2 ring-orange-300" : "border-red-200"}`} data-testid={`${testid}-${p.id}`}>
              <button type="button" onClick={() => onSelect && onSelect(p, null, null, `${testid}-${p.id}`)} title={p.reason} data-odak={`${testid}-${p.id}`}
                className={`w-full text-left px-2.5 py-1.5 text-xs font-bold transition-colors ${sel ? "bg-orange-500 text-white" : "bg-red-50 text-slate-900 hover:bg-red-100"}`}>{p.name}</button>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[10px]">
                  <thead>
                    <tr className="bg-slate-50">
                      <th className="px-2 py-1 text-left font-semibold text-slate-500 whitespace-nowrap">Hizmet / Ay</th>
                      {months.map((m) => <th key={m} className="px-1 py-1 text-center font-mono font-semibold text-slate-500 whitespace-nowrap">{fmtMonth(m)}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {cats.map((c) => (
                      <tr key={c} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-bold uppercase whitespace-nowrap text-slate-800" title={catStatus[c]?.reason}>{c}</td>
                        {months.map((m) => {
                          const ok = isDone(c, m);
                          const cs = p.claims_by_month?.[m]?.[c];
                          const state = stateOf(ok, cs);
                          const st = STATES[state];
                          const key = `${testid}-cell-${p.id}-${c}-${m}`;
                          return (
                            <td key={m} className="px-0.5 py-0.5 text-center">
                              <span role="button" tabIndex={0} data-testid={key} data-odak={key} data-state={state}
                                title={`${c} — ${fmtMonth(m)}: ${st.title} (${st.action}; ESC / İptal / Kaydet ile aynı filtrelerle bu hücreye dönülür)`}
                                onClick={(e) => { e.stopPropagation(); act(p, m, c, key, state, cs); }}
                                onKeyDown={(e) => { if (e.key === "Enter") act(p, m, c, key, state, cs); }}
                                className={`block w-full min-w-[64px] px-1 py-0.5 rounded font-semibold whitespace-nowrap outline-none cursor-pointer focus-visible:ring-2 focus-visible:ring-orange-400 ${st.cls}`}>{st.label}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
